// Client-side background removal - runs entirely in the browser via
// onnxruntime-web (WASM), no server round trip. Drop-in replacement for
// removeBackground.ts's same Promise<Blob> signature.
//
// Preprocessing/postprocessing here must match training/dataset.py and
// training/infer_test.py exactly (resize to INFER_SIZE, ImageNet
// normalization, bilinear-upscale the predicted alpha back to the
// original image size).
import * as ort from "onnxruntime-web";
import { guidedFilter } from "./guidedFilter";
import { removeStrayNoise } from "./maskCleanup";

// NOTE: deliberately NOT self-hosting these under public/ - onnxruntime-web's
// internal loader dynamically import()s its JSEP glue .mjs file, and Vite's
// dev server refuses to serve dynamically-imported files from publicDir
// ("should not be imported from source code... only referenced via HTML
// tags"). Loading from the default jsDelivr CDN sidesteps that restriction
// entirely (it's an external URL, not a local /public/ path). Pin the
// version to match package.json's onnxruntime-web so the CDN build matches
// what's bundled.
ort.env.wasm.wasmPaths = "https://cdn.jsdelivr.net/npm/onnxruntime-web@1.30.0/dist/";

const INFER_SIZE = 384;
const IMAGENET_MEAN = [0.485, 0.456, 0.406];
const IMAGENET_STD = [0.229, 0.224, 0.225];

export type ModelVariant = "small" | "medium";

const sessionCache = new Map<ModelVariant, Promise<ort.InferenceSession>>();

function getSession(variant: ModelVariant): Promise<ort.InferenceSession> {
    let session = sessionCache.get(variant);
    if (!session) {
        session = ort.InferenceSession.create(`/models/${variant}.onnx`, {
            executionProviders: ["wasm"],
        });
        sessionCache.set(variant, session);
    }
    return session;
}

async function loadImageBitmap(file: File): Promise<ImageBitmap> {
    return await createImageBitmap(file);
}

function preprocess(bitmap: ImageBitmap): Float32Array {
    const canvas = document.createElement("canvas");
    canvas.width = INFER_SIZE;
    canvas.height = INFER_SIZE;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(bitmap, 0, 0, INFER_SIZE, INFER_SIZE);
    const { data } = ctx.getImageData(0, 0, INFER_SIZE, INFER_SIZE);

    // HWC uint8 RGBA -> CHW float32 normalized, matching torchvision's
    // to_tensor() + normalize(IMAGENET_MEAN, IMAGENET_STD).
    const chw = new Float32Array(3 * INFER_SIZE * INFER_SIZE);
    const plane = INFER_SIZE * INFER_SIZE;
    for (let i = 0; i < plane; i++) {
        const r = data[i * 4] / 255;
        const g = data[i * 4 + 1] / 255;
        const b = data[i * 4 + 2] / 255;
        chw[i] = (r - IMAGENET_MEAN[0]) / IMAGENET_STD[0];
        chw[plane + i] = (g - IMAGENET_MEAN[1]) / IMAGENET_STD[1];
        chw[2 * plane + i] = (b - IMAGENET_MEAN[2]) / IMAGENET_STD[2];
    }
    return chw;
}

async function compositeAlpha(
    bitmap: ImageBitmap,
    alpha: Float32Array,
    width: number,
    height: number
): Promise<Blob> {
    // Render the INFER_SIZE alpha map onto a small canvas, then let the
    // browser's own bilinear scaling upsize it to the original resolution
    // (mirrors PIL's resize(..., Image.BILINEAR) used in infer_test.py).
    const small = document.createElement("canvas");
    small.width = INFER_SIZE;
    small.height = INFER_SIZE;
    const sctx = small.getContext("2d")!;
    const alphaImageData = sctx.createImageData(INFER_SIZE, INFER_SIZE);
    for (let i = 0; i < alpha.length; i++) {
        const v = Math.max(0, Math.min(255, Math.round(alpha[i] * 255)));
        alphaImageData.data[i * 4] = v;
        alphaImageData.data[i * 4 + 1] = v;
        alphaImageData.data[i * 4 + 2] = v;
        alphaImageData.data[i * 4 + 3] = 255;
    }
    sctx.putImageData(alphaImageData, 0, 0);

    const upscaled = document.createElement("canvas");
    upscaled.width = width;
    upscaled.height = height;
    const uctx = upscaled.getContext("2d")!;
    uctx.imageSmoothingEnabled = true;
    uctx.imageSmoothingQuality = "high";
    uctx.drawImage(small, 0, 0, width, height);
    const alphaMask = uctx.getImageData(0, 0, width, height);

    const out = document.createElement("canvas");
    out.width = width;
    out.height = height;
    const octx = out.getContext("2d")!;
    octx.drawImage(bitmap, 0, 0, width, height);
    const outData = octx.getImageData(0, 0, width, height);

    // Refine the naively-upscaled (blurry) alpha against the real full-res
    // photo: the guided filter snaps soft edges to the image's actual
    // high-frequency detail instead of just interpolating between the
    // coarse INFER_SIZE prediction's pixels.
    const n = width * height;
    const guide = new Float32Array(n);
    const naiveAlpha = new Float32Array(n);
    for (let i = 0; i < n; i++) {
        const r = outData.data[i * 4];
        const g = outData.data[i * 4 + 1];
        const b = outData.data[i * 4 + 2];
        guide[i] = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        naiveAlpha[i] = alphaMask.data[i * 4] / 255;
    }
    const refinedAlpha = guidedFilter(guide, naiveAlpha, width, height, 4, 0.01);

    // The guided filter's natural, edge-aware alpha is the best-looking
    // result on its own (confirmed across an 8-pipeline / 10-photo
    // comparison - heavier geometric cleanup (opening, convex-hull
    // protrusion removal, smoothing) consistently made edges WORSE, not
    // better, by shifting the boundary slightly and reintroducing
    // background-color fringing). The only thing layered on top is a
    // safety net: drop any stray disconnected blob the model spuriously
    // predicted elsewhere in the frame, without touching the shape of the
    // real subject's edge at all.
    const finalAlpha = removeStrayNoise(refinedAlpha, width, height);

    for (let i = 0; i < n; i++) {
        outData.data[i * 4 + 3] = Math.round(finalAlpha[i] * 255);
    }
    octx.putImageData(outData, 0, 0);

    return await new Promise<Blob>((resolve, reject) => {
        out.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))), "image/png");
    });
}

// Kicks off the (large, WASM) model download/compile ahead of the first
// upload so the UI can show a "loading the model" state up front instead of
// the user discovering the cold-start cost mid-upload. Safe to call more
// than once - getSession's cache means later callers (including the real
// removeBackgroundLocal call) just await the same in-flight/resolved promise.
export async function preloadModel(variant: ModelVariant = "medium"): Promise<void> {
    await getSession(variant);
}

export async function removeBackgroundLocal(file: File, variant: ModelVariant = "medium"): Promise<Blob> {
    const [session, bitmap] = await Promise.all([getSession(variant), loadImageBitmap(file)]);

    const inputTensor = new ort.Tensor("float32", preprocess(bitmap), [1, 3, INFER_SIZE, INFER_SIZE]);
    const outputs = await session.run({ input: inputTensor });
    const alpha = outputs.alpha.data as Float32Array;

    return await compositeAlpha(bitmap, alpha, bitmap.width, bitmap.height);
}
